import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Usuario, LoginCredentials, RegisterData } from '../types';
import authService from '../services/auth';
import toast from 'react-hot-toast';

interface AuthState {
  usuario: Usuario | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  
  // Actions
  login: (credentials: LoginCredentials) => Promise<boolean>;
  register: (data: RegisterData) => Promise<boolean>;
  logout: () => Promise<void>;
  loadUser: () => Promise<void>;
  updateUser: (data: Partial<Usuario>) => Promise<boolean>;
  setUser: (usuario: Usuario | null) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      usuario: authService.getStoredUser(),
      token: authService.getToken(),
      isLoading: false,
      isAuthenticated: authService.isAuthenticated(),

      login: async (credentials: LoginCredentials) => {
        set({ isLoading: true });
        try {
          const response = await authService.login(credentials);
          set({
            usuario: response.usuario,
            token: response.token,
            isAuthenticated: true,
            isLoading: false,
          });
          toast.success(`Bem-vindo(a), ${response.usuario.nome}!`);
          return true;
        } catch (error: any) {
          set({ isLoading: false });
          const message = error.response?.data?.message || 'Erro ao fazer login';
          toast.error(message);
          return false;
        }
      },

      register: async (data: RegisterData) => {
        set({ isLoading: true });
        try {
          const response = await authService.register(data);
          set({
            usuario: response.usuario,
            token: response.token,
            isAuthenticated: true,
            isLoading: false,
          });
          toast.success('Conta criada com sucesso!');
          return true;
        } catch (error: any) {
          set({ isLoading: false });
          const message = error.response?.data?.message || 'Erro ao criar conta';
          toast.error(message);
          return false;
        }
      },

      logout: async () => {
        set({ isLoading: true });
        try {
          await authService.logout();
        } finally {
          set({
            usuario: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
          });
          toast.success('Você saiu da sua conta');
        }
      },

      loadUser: async () => {
        if (!get().token) return;
        
        set({ isLoading: true });
        try {
          const usuario = await authService.getProfile();
          set({
            usuario,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (error) {
          set({
            usuario: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
          });
        }
      },

      updateUser: async (data: Partial<Usuario>) => {
        set({ isLoading: true });
        try {
          const usuario = await authService.updateProfile(data);
          set({
            usuario,
            isLoading: false,
          });
          toast.success('Perfil atualizado com sucesso!');
          return true;
        } catch (error: any) {
          set({ isLoading: false });
          const message = error.response?.data?.message || 'Erro ao atualizar perfil';
          toast.error(message);
          return false;
        }
      },

      setUser: (usuario: Usuario | null) => {
        set({ usuario, isAuthenticated: !!usuario });
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        token: state.token,
        usuario: state.usuario,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

export default useAuthStore;
